import React, { useEffect, useState, useCallback, useContext } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { synthesizeSpeech } from '../lib/openai';
import { AppContext } from '../context/AppContext';

export const VoiceControl = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const { isRecording, startRecording, stopRecording } = useVoiceRecording();
  const { setShowStats, setIsRunning } = useContext(AppContext);

  const speak = useCallback(async (text: string) => {
    try {
      const audioData = await synthesizeSpeech(text);
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(audioData);
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start(0);
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  }, []);

  const handleVoiceCommand = useCallback(async (command: string) => {
    const normalizedCommand = command.toLowerCase().trim();
    
    if (normalizedCommand.includes('hello') || normalizedCommand.includes('hi')) {
      await speak("Hi! Are we running today?");
    } else if (normalizedCommand.includes('show stats')) {
      setShowStats(true);
      await speak("Displaying your current running statistics.");
    } else if (normalizedCommand.includes('hide stats')) {
      setShowStats(false);
      await speak("Hiding statistics.");
    } else if (normalizedCommand.includes('next song')) {
      await speak("Skipping to the next song that matches your pace.");
    } else if (normalizedCommand.includes("what's my pace")) {
      await speak("Your current pace is 8 minutes and 24 seconds per kilometer. You're maintaining a steady rhythm.");
    } else if (normalizedCommand.includes('end run')) {
      await speak("Ending your run now.");
      setIsRunning(false);
    } else {
      await speak("I didn't catch that. Could you please repeat?");
    }
  }, [speak, setShowStats, setIsRunning]);

  const toggleListening = useCallback(async () => {
    if (!isListening) {
      setIsListening(true);
      await speak('Voice control activated. I\'m listening.');
      await startRecording();
    } else {
      setIsListening(false);
      const transcription = await stopRecording();
      if (transcription) {
        setTranscript(transcription);
        setShowTranscript(true);
        await handleVoiceCommand(transcription);
        setTimeout(() => setShowTranscript(false), 3000);
      }
    }
  }, [isListening, startRecording, stopRecording, speak, handleVoiceCommand]);

  useEffect(() => {
    return () => {
      if (isListening) {
        stopRecording();
      }
    };
  }, [isListening, stopRecording]);

  return (
    <div className="relative">
      <button
        onClick={toggleListening}
        className={`glass-button p-4 ${isListening ? 'bg-white/20' : ''}`}
      >
        {isListening ? (
          <Mic className="w-6 h-6 text-green-400" />
        ) : (
          <MicOff className="w-6 h-6" />
        )}
      </button>
      
      <AnimatePresence>
        {showTranscript && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute bottom-full mb-2 left-0 glass p-2 text-sm whitespace-nowrap"
          >
            {transcript}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};